import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, MoreHorizontal, Copy, Trash2, RefreshCw, Pencil, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PanelHeader } from "./PagesPanel";
import { useEditor } from "./useEditorState";
import DeleteAssetDialog from "./DeleteAssetDialog";
import { assetUrl, errMsg } from "@/lib/api";

const FILTERS = [["", "Tutti"], ["image", "Immagini"], ["logo", "Loghi"], ["reference", "Riferimenti"], ["document", "Documenti"]];

export default function AssetsPanel() {
  const { api, id, assets, setAssets, save, frameRef } = useEditor();
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const replaceInput = useRef(null);
  const replacing = useRef(null);

  const upload = async (files) => {
    setBusy(true);
    for (const f of Array.from(files)) {
      const fd = new FormData(); fd.append("file", f); fd.append("kind", /logo/i.test(f.name) ? "logo" : f.type.startsWith("image/") ? "image" : "document");
      try { const { data } = await api.post(`/projects/${id}/assets`, fd); setAssets((xs) => [data, ...xs]); } catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    setBusy(false);
  };
  const replace = async (file) => {
    const a = replacing.current; if (!a || !file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/assets/${a.id}/replace`, fd); setAssets((xs) => xs.map((x) => (x.id === a.id ? data : x))); toast.success("Immagine sostituita ovunque sia usata"); } catch (e) { toast.error(errMsg(e)); }
  };
  const rename = async (a) => {
    const name = window.prompt("Nuovo nome del file", a.name); if (!name || name === a.name) return;
    try { const { data } = await api.patch(`/assets/${a.id}`, { name }); setAssets((xs) => xs.map((x) => (x.id === a.id ? data : x))); } catch (e) { toast.error(errMsg(e)); }
  };
  const setKind = async (a, kind) => {
    const { data } = await api.patch(`/assets/${a.id}`, { kind }); setAssets((xs) => xs.map((x) => (x.id === a.id ? data : x)));
  };
  const [toDelete, setToDelete] = useState(null);
  const remove = (a) => setToDelete(a);
  const copy = (a) => navigator.clipboard.writeText(a.url).then(() => toast.success("URL copiato: incollalo in una proprietà immagine o nel codice"));
  const shown = assets.filter((a) => !filter || a.kind === filter);

  return (
    <div data-testid="assets-panel">
      <DeleteAssetDialog api={api} asset={toDelete} prepare={save} onClose={() => setToDelete(null)} onDeleted={(a) => { setAssets((xs) => xs.filter((x) => x.id !== a.id)); frameRef.current?.send({ type: "assetGone", id: a.id }); toast.success(`«${a.name}» eliminata`); }} />
      <PanelHeader title="Asset" subtitle={`${assets.length} file di questo progetto`} action={
        <Button size="sm" className="h-8" variant="outline" onClick={() => input.current?.click()} disabled={busy} data-testid="upload-asset-btn">{busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}</Button>} />
      <input ref={input} type="file" multiple hidden accept="image/*,.pdf,.doc,.docx,.txt" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="asset-file-input" />
      <input ref={replaceInput} type="file" hidden accept="image/*" onChange={(e) => { replace(e.target.files[0]); e.target.value = ""; }} />
      <div className="px-4 flex flex-wrap gap-1">
        {FILTERS.map(([k, l]) => <button key={k || "all"} onClick={() => setFilter(k)} className={`text-[11px] px-2 py-1 rounded-full border ${filter === k ? "bg-navy-900 text-white border-navy-900" : "text-slate-600 border-slate-200"}`}>{l}</button>)}
      </div>
      <div className="p-3 grid grid-cols-2 gap-2" data-testid="assets-grid">
        {shown.map((a) => (
          <div key={a.id} className="group rounded-lg border bg-white overflow-hidden" data-testid={`asset-item-${a.id}`}>
            <div className="aspect-square bg-[repeating-conic-gradient(#f1f5f9_0_25%,#fff_0_50%)] bg-[length:14px_14px] flex items-center justify-center">
              {a.content_type?.startsWith("image/") ? <img src={assetUrl(a.url)} alt={a.name} className="max-w-full max-h-full object-contain" /> : <FileText className="text-slate-400" />}
            </div>
            <div className="flex items-center gap-1 px-2 py-1.5">
              <p className="flex-1 min-w-0 text-[11px] text-slate-700 truncate" title={a.name}>{a.name}</p>
              <DropdownMenu>
                <DropdownMenuTrigger className="p-0.5 text-slate-400 hover:text-slate-800" data-testid={`asset-menu-${a.id}`}><MoreHorizontal size={14} /></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => copy(a)}><Copy size={14} className="mr-2" />Copia URL</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => rename(a)}><Pencil size={14} className="mr-2" />Rinomina</DropdownMenuItem>
                  {a.content_type?.startsWith("image/") && <DropdownMenuItem onClick={() => { replacing.current = a; replaceInput.current?.click(); }}><RefreshCw size={14} className="mr-2" />Sostituisci</DropdownMenuItem>}
                  {["image", "logo", "reference"].filter((k) => k !== a.kind).map((k) => <DropdownMenuItem key={k} onClick={() => setKind(a, k)}>Segna come {FILTERS.find((f) => f[0] === k)[1].toLowerCase()}</DropdownMenuItem>)}
                  <DropdownMenuItem className="text-red-600" onClick={() => remove(a)} data-testid={`asset-delete-${a.id}`}><Trash2 size={14} className="mr-2" />Elimina</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        ))}
      </div>
      {!shown.length && <p className="px-4 text-xs text-slate-500">Nessun file. Carica logo e immagini: restano separati per progetto.</p>}
    </div>
  );
}
